import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";

// API Configuration.
// The previous fallback pointed at trading-backend-bekp.onrender.com, which no
// longer serves this API (it 404s). A missing VITE_API_BASE_URL is a
// misconfiguration, so surface it loudly instead of silently addressing a host
// that cannot answer.
const PRODUCTION_API_URL = "https://ticker-api.satech.cloud/api/";

const resolveApiBaseUrl = (): string => {
  const configured = import.meta.env.VITE_API_BASE_URL;
  if (configured) return configured;

  // In development an unset URL is a mistake worth stopping for.
  if (import.meta.env.DEV) {
    throw new Error(
      "VITE_API_BASE_URL is not set. Copy .env.example to .env.local and point it at your API.",
    );
  }

  // In production, a blank page helps nobody. Fall back to the live API and
  // make the misconfiguration loud in the console instead.
  console.error(
    `VITE_API_BASE_URL was not set at build time; falling back to ${PRODUCTION_API_URL}`,
  );
  return PRODUCTION_API_URL;
};

const API_BASE_URL = resolveApiBaseUrl();

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem("token");

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

/**
 * Endpoints where a 401 is a normal answer ("those credentials are wrong")
 * rather than an expired session. Signing the user out on these would wipe
 * their session the moment they fat-finger a password.
 */
const CREDENTIAL_ENDPOINTS = [
  "/auth/login",
  "/auth/register",
  "/auth/google",
  "/auth/forgot-password",
  "/auth/reset-password",
];

const isCredentialRequest = (url?: string) =>
  !!url && CREDENTIAL_ENDPOINTS.some((endpoint) => url.includes(endpoint));

/** Clear every trace of the session, including the persisted zustand store. */
export const clearStoredAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("auth-storage");
};

// Guard against a burst of concurrent 401s (the dashboard polls several
// endpoints at once) each triggering its own redirect.
let redirectingToLogin = false;

// Response interceptor - sign the user out when their session expires
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | InternalAxiosRequestConfig
      | undefined;

    // This used to attempt a token refresh against POST /auth/refresh. That
    // endpoint does not exist and the API never issues a refresh token, so the
    // branch was unreachable: an expired session left the user on a dashboard
    // where every poll failed silently and they were never signed out. Treat a
    // 401 on an authenticated request as the end of the session.
    if (
      error.response?.status === 401 &&
      !isCredentialRequest(originalRequest?.url) &&
      !redirectingToLogin
    ) {
      redirectingToLogin = true;
      clearStoredAuth();

      // Come back to where they were once they have signed in again.
      const returnTo = `${window.location.pathname}${window.location.search}`;
      const target =
        returnTo && returnTo !== "/"
          ? `/login?returnTo=${encodeURIComponent(returnTo)}`
          : "/login";

      window.location.assign(target);
    }

    return Promise.reject(error);
  },
);

export default apiClient;

/**
 * An API failure that keeps its HTTP status.
 *
 * Callers need to tell "your token is invalid" (401) apart from "you are being
 * throttled" (429) or a network blip. Throwing a bare Error discarded that, and
 * the auth store consequently treated every failure as a bad token and signed
 * the user out.
 */
export class ApiError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/** Normalise anything thrown by axios into an ApiError. */
export const toApiError = (error: unknown): ApiError => {
  const message = handleApiError(error);

  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { code?: string } | undefined;
    return new ApiError(message, error.response?.status, data?.code);
  }

  return new ApiError(message);
};

// Helper function to handle API errors
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    if (axiosError.response?.status === 404) {
      return "Resource not found";
    }

    if (axiosError.response?.status === 500) {
      return "Server error. Please try again later.";
    }

    if (axiosError.message === "Network Error") {
      return "Network error. Please check your connection.";
    }

    return axiosError.message || "An unexpected error occurred";
  }

  return "An unexpected error occurred";
};
